import React, { useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import classNames from "../../../utils/classNames";
import BandCatalogCard from "../../molecules/BandCatalogCard";
import BandCatalogToolbar from "../../molecules/BandCatalogToolbar";
import LoadingSpinner from "../../atoms/LoadingSpinner";
import VirtualizedItem from "../../molecules/VirtualizedItem";
import useInfiniteScrollObserver from "../../../hooks/useInfiniteScrollObserver";
import {
    bandHasStyleTag,
    normalizeStyleTagKey,
    normalizeStyleTagList,
} from "../../../utils/styleTags";
import {
    buildSupportedCountryAliases,
    buildUsStateAliases,
} from "../../../utils/location";
import { getActiveBandSeekingDetails } from "../../../utils/bandSeeking";
import { resolveUserDisplayName, resolveUserUsername } from "../../../utils/userIdentity";
import "./index.scss";

const normalize = (value) => (value || "").toString().trim().toLowerCase();
const uniqueNormalizedTerms = (values) => [...new Set((values || []).map(normalize).filter(Boolean))];

const buildLocationFilterTerms = (location, selectedLocation) => {
    if (selectedLocation) {
        const cityTerms = uniqueNormalizedTerms([selectedLocation.cityName || selectedLocation.city]);
        const regionTerms = uniqueNormalizedTerms(buildUsStateAliases(
            selectedLocation.regionName || selectedLocation.stateOrRegion,
        ));
        const hasSpecificTerms = cityTerms.length > 0 || regionTerms.length > 0;
        const countryTerms = hasSpecificTerms
            ? []
            : uniqueNormalizedTerms(buildSupportedCountryAliases({
                countryName: selectedLocation.countryName || selectedLocation.country,
                countryCode: selectedLocation.countryCode,
            }));

        return [cityTerms, regionTerms, countryTerms].filter((terms) => terms.length > 0);
    }

    const typedLocation = normalize(location);
    return typedLocation ? [[typedLocation]] : [];
};

function bandMatchesSearch(band, term) {
    if (!term) return true;
    const haystack = [
        band?.name,
        band?.style,
        band?.description,
        band?.location,
        band?.locationSearchText,
        ...(band?.styleTags || []),
        ...((band?.members || []).flatMap((member) => [
            resolveUserDisplayName(member),
            resolveUserUsername(member),
        ])),
    ].filter(Boolean).map(normalize).join(" ");
    return haystack.includes(term);
}

function bandMatchesGenres(band, genres) {
    const normalizedGenres = normalizeStyleTagList(genres);
    if (!normalizedGenres.length) return true;

    const bandStyleTags = normalizeStyleTagList([
        ...(Array.isArray(band?.styleTags) ? band.styleTags : []),
        band?.style,
    ]);

    return normalizedGenres.some((genre) => bandHasStyleTag({ styleTags: bandStyleTags }, genre));
}

function bandMatchesLocation(band, locationTerms) {
    if (!locationTerms.length) return true;
    const searchableLocation = normalize([band?.location, band?.locationSearchText].filter(Boolean).join(" "));
    const searchableLocationTokens = new Set(searchableLocation.split(/[^a-z0-9]+/).filter(Boolean));
    const matchesTerm = (term) => (
        term.length <= 2
            ? searchableLocationTokens.has(term)
            : searchableLocation.includes(term)
    );
    return locationTerms.every((termGroup) => termGroup.some(matchesTerm));
}

function bandMatchesSeekingMembers(band, lookingForMembers, memberRoles) {
    if (!lookingForMembers) return true;

    const seekingDetails = getActiveBandSeekingDetails({
        seekingMembers: band?.seekingMembers,
        instrumentsNeeded: band?.instrumentsNeeded,
    });

    if (!seekingDetails) return false;

    const normalizedMemberRoles = normalizeStyleTagList(memberRoles).map(normalizeStyleTagKey);
    if (!normalizedMemberRoles.length) return true;

    const bandMemberRoles = new Set(
        normalizeStyleTagList((seekingDetails.members || []).map((member) => member?.instrument))
            .map(normalizeStyleTagKey),
    );

    return normalizedMemberRoles.some((role) => bandMemberRoles.has(role));
}

export default function BandsCatalog({
    bands,
    onOpenBand,
    className = "",
    hasMore = false,
    isLoadingMore = false,
    onLoadMore,
    emptyTitle = undefined,
    emptyDescription = undefined,
    filterTogglePortalTarget = null,
    renderFilterToggleInToolbar = true,
    filterToggleIconOnly = false,
    renderCardActions,
}) {
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState("");
    const [genres, setGenres] = useState([]);
    const [draftGenres, setDraftGenres] = useState([]);
    const [location, setLocation] = useState("");
    const [draftLocation, setDraftLocation] = useState("");
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [draftSelectedLocation, setDraftSelectedLocation] = useState(null);
    const [lookingForMembers, setLookingForMembers] = useState(false);
    const [draftLookingForMembers, setDraftLookingForMembers] = useState(false);
    const [memberRoles, setMemberRoles] = useState([]);
    const [draftMemberRoles, setDraftMemberRoles] = useState([]);

    const handleResetFilters = useCallback(() => {
        setGenres([]);
        setDraftGenres([]);
        setLocation("");
        setDraftLocation("");
        setSelectedLocation(null);
        setDraftSelectedLocation(null);
        setLookingForMembers(false);
        setDraftLookingForMembers(false);
        setMemberRoles([]);
        setDraftMemberRoles([]);
    }, []);

    const handleDraftLocationChange = useCallback((nextLocation) => {
        setDraftLocation(nextLocation);
        setDraftSelectedLocation(null);
    }, []);

    const handleDraftLocationSelect = useCallback((geocodedLocation) => {
        if (!geocodedLocation) {
            setDraftSelectedLocation(null);
            return;
        }
        setDraftSelectedLocation(geocodedLocation);
        setDraftLocation(geocodedLocation.label || "");
    }, []);

    const handleDraftLookingForMembersChange = useCallback((nextValue) => {
        const isLookingForMembers = Boolean(nextValue);
        setDraftLookingForMembers(isLookingForMembers);
        if (!isLookingForMembers) {
            setDraftMemberRoles([]);
        }
    }, []);

    const handleApplyFilters = useCallback((nextFilters = {}) => {
        const nextGenres = Array.isArray(nextFilters?.genres)
            ? normalizeStyleTagList(nextFilters.genres)
            : draftGenres;
        const nextLookingForMembers = typeof nextFilters?.lookingForMembers === "boolean"
            ? nextFilters.lookingForMembers
            : draftLookingForMembers;
        const nextMemberRoles = nextLookingForMembers
            ? normalizeStyleTagList(
                Array.isArray(nextFilters?.memberRoles) ? nextFilters.memberRoles : draftMemberRoles,
            )
            : [];

        setGenres(nextGenres);
        setDraftGenres(nextGenres);
        setLocation(draftLocation);
        setSelectedLocation(draftSelectedLocation);
        setLookingForMembers(nextLookingForMembers);
        setDraftLookingForMembers(nextLookingForMembers);
        setMemberRoles(nextMemberRoles);
        setDraftMemberRoles(nextMemberRoles);
    }, [draftGenres, draftLocation, draftMemberRoles, draftLookingForMembers, draftSelectedLocation]);

    const canReset = Boolean(
        draftGenres.length
            || draftLocation
            || draftLookingForMembers
            || draftMemberRoles.length
            || genres.length
            || location
            || lookingForMembers
            || memberRoles.length,
    );
    const hasAppliedFilters = Boolean(genres.length || location || lookingForMembers || memberRoles.length);

    const filtered = useMemo(() => {
        const normalizedSearch = normalize(searchTerm);
        const locationTerms = buildLocationFilterTerms(location, selectedLocation);
        return (bands || []).filter((band) => (
            bandMatchesSearch(band, normalizedSearch)
                && bandMatchesGenres(band, genres)
                && bandMatchesLocation(band, locationTerms)
                && bandMatchesSeekingMembers(band, lookingForMembers, memberRoles)
        ));
    }, [bands, searchTerm, genres, location, selectedLocation, lookingForMembers, memberRoles]);

    const loadMoreSentinelRef = useInfiniteScrollObserver({
        enabled: filtered.length > 0,
        hasMore,
        isLoading: isLoadingMore,
        onLoadMore,
    });

    return (
        <section className={classNames("bands-catalog", className)} aria-label={t("bandsWelcomePage.catalog.aria")}>
            <BandCatalogToolbar
                className="bands-catalog__toolbar"
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                genres={draftGenres}
                onGenresChange={setDraftGenres}
                location={draftLocation}
                onLocationChange={handleDraftLocationChange}
                onLocationSelect={handleDraftLocationSelect}
                lookingForMembers={draftLookingForMembers}
                onLookingForMembersChange={handleDraftLookingForMembersChange}
                memberRoles={draftMemberRoles}
                onMemberRolesChange={setDraftMemberRoles}
                onReset={handleResetFilters}
                onApply={handleApplyFilters}
                canReset={canReset}
                hasAppliedFilters={hasAppliedFilters}
                filterTogglePortalTarget={filterTogglePortalTarget}
                renderFilterToggleInToolbar={renderFilterToggleInToolbar}
                filterToggleIconOnly={filterToggleIconOnly}
            />

            {filtered.length > 0 ? (
                <div className="bands-catalog__grid" role="list">
                    {filtered.map((band) => (
                        <div key={band.id} role="listitem" className="bands-catalog__cell">
                            <VirtualizedItem
                                estimateHeight={360}
                                className="bands-catalog__virtual-item"
                            >
                                <BandCatalogCard
                                    band={band}
                                    onOpen={onOpenBand}
                                    actions={typeof renderCardActions === "function" ? renderCardActions(band) : null}
                                />
                            </VirtualizedItem>
                        </div>
                    ))}
                    {hasMore || isLoadingMore ? (
                        <div className="bands-catalog__load-more" ref={loadMoreSentinelRef}>
                            {isLoadingMore ? (
                                <LoadingSpinner />
                            ) : (
                                <button type="button" onClick={onLoadMore}>
                                    {t("bandsWelcomePage.catalog.loadMore")}
                                </button>
                            )}
                        </div>
                    ) : null}
                </div>
            ) : (
                <div className="bands-catalog__empty" role="status">
                    <p className="bands-catalog__empty-title">
                        {emptyTitle || t("bandsWelcomePage.catalog.emptyTitle")}
                    </p>
                    <p className="bands-catalog__empty-description">
                        {emptyDescription || t("bandsWelcomePage.catalog.emptyDescription")}
                    </p>
                    {hasMore || isLoadingMore ? (
                        <div className="bands-catalog__load-more" ref={loadMoreSentinelRef}>
                            {isLoadingMore ? (
                                <LoadingSpinner />
                            ) : (
                                <button type="button" onClick={onLoadMore}>
                                    {t("bandsWelcomePage.catalog.loadMore")}
                                </button>
                            )}
                        </div>
                    ) : null}
                </div>
            )}
        </section>
    );
}

BandsCatalog.propTypes = {
    bands: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string,
        seekingMembers: PropTypes.object,
        instrumentsNeeded: PropTypes.object,
    })).isRequired,
    onOpenBand: PropTypes.func,
    className: PropTypes.string,
    hasMore: PropTypes.bool,
    isLoadingMore: PropTypes.bool,
    onLoadMore: PropTypes.func,
    emptyTitle: PropTypes.string,
    emptyDescription: PropTypes.string,
    filterTogglePortalTarget: PropTypes.object,
    renderFilterToggleInToolbar: PropTypes.bool,
    filterToggleIconOnly: PropTypes.bool,
    renderCardActions: PropTypes.func,
};
